/* eslint-disable no-console */
import { RBIEnv } from 'utils/environment/types';

/** CLI TESTER
 *  Usage: tsx get-dynamic-app-config.ts <region> <env>
 */
if (global?.process?.argv?.join(' ').includes('get-dynamic-app-config.ts')) {
  const [, , regionArg, envArg] = process.argv;

  if (!regionArg || !envArg) {
    console.error('Usage: npx tsx get-dynamic-app-config.ts <region> <env>');
    process.exit(1);
  }

  const region = regionArg.toUpperCase() as 'US' | 'CA';
  const env = envArg.toLowerCase() as RBIEnv;

  console.log('\x1b[32m%s\x1b[0m', `Config for region: ${region}, env: ${env}\n`);

  const cfg = getDynamicAppConfig(region, env);
  console.log(JSON.stringify(cfg, null, 2));
}

/**
 * gets config that varies by region or env
 */
export function getDynamicAppConfig(region: 'US' | 'CA', env: RBIEnv) {
  const byRegion = <T>(configByRegion: { CA: T; US: T; default?: T }): T =>
    configByRegion[region] ?? (configByRegion.default as T);

  const byEnv = <T>(configByEnv: {
    dev?: T;
    qa?: T;
    prod?: T;
    staging?: T;
    prodOrQa?: T;
    default?: T;
  }): T => {
    const envVal = configByEnv[env];
    if (envVal !== undefined && envVal !== null) {
      return configByEnv[env] as T;
    }

    if ((env === 'prod' || env === 'qa') && configByEnv.prodOrQa) {
      return configByEnv.prodOrQa;
    }

    return configByEnv.default as T;
  };

  return {
    brand: 'bk',
    env,
    buildRegion: region,

    apiKeys: {
      amplitude: byRegion({
        US: byEnv({
          prod: 'a86daf403b38435c59d33b9ef9ee95ed',
          default: 'aead0cc4221a704ce01ad72968256851',
        }),
        CA: byEnv({
          prod: '66bedea4438c75fcb66c781022941573',
          default: '1d58811e0cf06cbd039e505faea59a82',
        }),
      }),
      amplitudeExperiments: byRegion({
        US: byEnv({
          prod: 'client-vIID5MOaaxNBFPg3IP9j8BHg4oMdz20v',
          default: 'client-tuV7JoxOo7qeBVlVBAv4JkK1DdH4BAsH',
        }),
        CA: byEnv({
          prod: 'client-qa2YGu01SRbRMqQNYBE0jzfdmIWbXvBI',
          default: 'client-F5xvRlekiRkhfu8cIW37iqn2Tc9zXnEr',
        }),
      }),
      branch: byRegion({
        US: byEnv({
          prodOrQa: 'key_live_pjC6MwRAi7CX8J1F6MMbKcdoEElw8ztg',
          default: 'key_test_heyZVwLzaWv08S8DWLNm9dhaxEdy4qtt',
        }),
        CA: byEnv({
          prodOrQa: 'key_live_ajPWhsHa6RXLEPc89JHAqdpltxo2q1lC',
          default: 'key_test_efL6ntIh3KYVCOk31VHCDpgoCFe9D5dV',
        }),
      }),
      braze: byRegion({
        US: byEnv({
          prodOrQa: 'e1f5b376-c0ff-46b4-9042-01ffac3ec488',
        }),
        CA: byEnv({
          prodOrQa: '896c405d-24f0-4e66-af86-3d2192f5462e',
        }),
        default: '0170377f-e405-4c7a-ab80-005dc46d1228',
      }),
      bringg: '', // only staging US had a value so setting empty. this seems to be broken in prod right now with no key. STAGING value: ue4g_yYRGtPs-iZJw1JEHY1mH
      googleMaps: 'AIzaSyATcsWnJUNiEC1the-_wjs-M_qy8YtA7rc',
      googleMapsWeb: byEnv({
        prod: 'AIzaSyAIA6S6eW3gSe9T2e4l_eaa47w6LGEGwmY',
        default: 'AIzaSyBHMkhePwX_a7ilVMPPdp-ChoOcS1q-Rm0',
      }),
      googleMapsIOS: byEnv({
        prod: 'AIzaSyBQZjsO1KZfbX8JPVFiNJZbpNT8OGx2BsI',
        default: 'AIzaSyCl8r4pvCc5SxrHSOJgZQqH-jRadjqFUHQ',
      }),
      googleMapsAndroid: byEnv({
        prod: 'AIzaSyAa4R5i43-lZ2WrnIgbHMmDVzzlFFKm9Q8',
        default: 'AIzaSyC27pxsJ4TVd9nLjel-VldCV7qNZJ5udgY',
      }),
      launchDarkly: byEnv({
        dev: '639b5f8ccd8316120130f479',
        qa: '639b5f9f66334d11b36f891f',
        staging: '639b715c3e4b9511760aaaf0',
        prod: '639b5fee9bf9a7117a1ee9cb',
      }),
      launchDarklyMobileKey: byEnv({
        dev: 'mob-b170ab70-6a77-40b5-bdc0-60ff8f25c61c',
        qa: 'mob-b5a7fc18-10e9-4f6c-a4e2-3274f9047166',
        staging: 'mob-b66ff32e-8c9f-44c2-83e7-dd50ce3da8b0',
        prod: 'mob-f76d0955-0705-48be-b27c-2fe77e6a061b',
      }),
    },
    apple: byRegion({
      US: {
        merchantId: byEnv({
          prodOrQa: 'merchant.com.bk.native.tillster.prod',
          default: 'merchant.com.bk.native.tillster',
        }),
        prepaidMerchantId: byEnv({
          prodOrQa: 'merchant.com.bk.native.tillster.prod',
          default: 'merchant.com.bk.native.tillster',
        }),
        migrationMerchantId: '',
        paymentsNetworks: ['AmEx', 'MasterCard', 'Visa', 'Discover'],
        mapKitWebJSToken: byEnv({
          prod:
            'eyJraWQiOiI3VDZSUkdKODU5IiwidHlwIjoiSldUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiJLQUw1OUtXOVVOIiwiaWF0IjoxNzcyNTU2MTg4LCJvcmlnaW4iOiJ3d3cuYmsuY29tIn0.NfLhiytGWQiWm7flZS3owMOOrs116BDNA_xpoQi1ZbM46Y0H2s3E1ondWN2Sh08tt3-LSWQuZ2NsRQIoFpfjpQ',
          default:
            'eyJraWQiOiI0REw0Q0hWR0tDIiwidHlwIjoiSldUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiJLQUw1OUtXOVVOIiwiaWF0IjoxNzY5MTc5Njk1LCJvcmlnaW4iOiIqLnJiaS50b29scyJ9.8akS5ILM6-fRLFJjVHQHryCi2kTW4Ur3gN5nrIQAnBzOJKaK5PEoWx-vOvJ9kqg2LW6vHO7PnTyWSY_oCFwEfg',
        }),
      },
      CA: {
        merchantId: byEnv({
          prodOrQa: 'merchant.com.bk.ca.native.orbital.prod',
          default: 'merchant.com.bk.ca.native.orbital.test',
        }),
        migrationMerchantId: byEnv({
          prodOrQa: 'merchant.com.bk.ca.native.orbital.prod',
          default: 'merchant.com.bk.ca.native.orbital.test',
        }),
        prepaidMerchantId: '',
        paymentsNetworks: ['AmEx', 'MasterCard', 'Visa', 'Discover'],
        mapKitWebJSToken: byEnv({
          prod:
            'eyJraWQiOiJWR1lDTENVMjZBIiwidHlwIjoiSldUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiJLQUw1OUtXOVVOIiwiaWF0IjoxNzcyNTU2MzQ5LCJvcmlnaW4iOiJ3d3cuYnVyZ2Vya2luZy5jYSJ9.fxjvBd8OCNr0AqDHolhYNjiZCGI0L6A0j-l01DE6CFk41UuYmwggyF9Vv516J_R5nMFKmxyWiVntx4SqCIV3tg',
          default:
            'eyJraWQiOiI0REw0Q0hWR0tDIiwidHlwIjoiSldUIiwiYWxnIjoiRVMyNTYifQ.eyJpc3MiOiJLQUw1OUtXOVVOIiwiaWF0IjoxNzY5MTc5Njk1LCJvcmlnaW4iOiIqLnJiaS50b29scyJ9.8akS5ILM6-fRLFJjVHQHryCi2kTW4Ur3gN5nrIQAnBzOJKaK5PEoWx-vOvJ9kqg2LW6vHO7PnTyWSY_oCFwEfg',
        }),
      },
    }),

    appleWebClientId: byRegion({
      US: byEnv({
        dev: {
          client_id: 'com.bk.us.dev',
          redirect_uri: 'https://main-bk-us-web.com.rbi.tools/signin',
        },
        qa: {
          client_id: 'com.bk.us.qa',
          redirect_uri: 'https://rc-bk-us-web.com.rbi.tools/signin',
        },
        staging: {
          client_id: 'com.bk.us.staging',
          redirect_uri: 'https://main-bk-us-web.com.rbi.tools/signin',
        },
        prod: {
          client_id: 'com.bk.us',
          redirect_uri: 'https://www.bk.com/signin',
        },
      }),
      CA: byEnv({
        dev: {
          client_id: 'com.bk.ca.dev',
          redirect_uri: 'https://main-bk-us-web.com.rbi.tools/signin',
        },
        qa: {
          client_id: 'com.bk.ca.qa',
          redirect_uri: 'https://rc-bk-us-web.com.rbi.tools/signin',
        },
        prod: {
          client_id: 'com.bk.ca',
          redirect_uri: 'https://www.burgerking.ca/signin',
        },
      }),
    }),

    appStoreUrl: byRegion({
      US: 'https://apps.apple.com/us/app/burger-king-app/id638323895',
      CA: 'https://apps.apple.com/us/app/burger-king-canada/id1422864779',
    }),
    playStoreUrl: byRegion({
      US: 'https://play.google.com/store/apps/details?id=com.emn8.mobilem8.nativeapp.bk',
      CA: 'https://play.google.com/store/apps/details?id=com.emn8.mobilem8.nativeapp.bkca&pli=1',
    }),

    androidIntentFilterHosts: byRegion({
      US: ['www.bk.com', 'bk.com'],
      CA: ['www.burgerking.ca', 'burgerking.ca'],
    }),
    associatedDomains: byRegion({
      US: [
        'alpha.bk.com',
        'beta.bk.com',
        'bk.com',
        'www.bk.com',
        'www.burgerking.com',
        'burgerking.test-app.link',
        'burgerking-alternate.test-app.link',
        'burgerking.app.link',
        'burgerking-alternate.app.link',
        'ablink.thekingdom.bk.com',
        '8147563.thekingdom.bk.com',
        'ablink.your-way.bk.com',
        '8147563.your-way.bk.com',
        'ablink.1954.bk.com',
        '8147563.1954.bk.com',
        'dev-bk-web.com.rbi.tools',
        'qa-bk-web.com.rbi.tools',
        'staging-bk-web.com.rbi.tools',
        'prod-bk-web.com.rbi.tools',
      ],
      CA: [
        'burgerking.ca',
        'www.burgerking.ca',
        '*.web.bk.ca.rbi.tools',
        'link.mail.burgerking.ca',
        'link.yourway.burgerking.ca',
        'burgerkingca.app.link',
        'burgerkingca-alternate.app.link',
        'burgerkingca.test-app.link',
        'burgerkingca-alternate.test-app.link',
      ],
    }),
    branchDomains: byRegion({
      US: [
        'burgerking.test-app.link',
        'burgerking-alternate.test-app.link',
        'burgerking.app.link',
        'burgerking-alternate.app.link',
      ],
      CA: [
        'burgerkingca.app.link',
        'burgerkingca-alternate.app.link',
        'burgerkingca.test-app.link',
        'burgerkingca-alternate.test-app.link',
      ],
    }),
    aws: {
      gqlApiUrl: byEnv({
        dev: 'https://use1-dev-bk.rbictg.com/graphql',
        qa: 'https://use1-qa-bk.rbictg.com/graphql',
        staging: 'https://use1-staging-bk.rbictg.com/graphql',
        prod: 'https://use1-prod-bk.rbictg.com/graphql',
      }),
      gqlGatewayApiUrl: byEnv({
        dev: 'https://use1-dev-bk-gateway.rbictg.com/graphql',
        qa: 'https://use1-qa-bk-gateway.rbictg.com/graphql',
        staging: 'https://use1-staging-bk-gateway.rbictg.com/graphql',
        prod: 'https://use1-prod-bk-gateway.rbictg.com/graphql',
      }),
      userPoolClientId: byEnv({
        dev: '327tmp31adanpc3mei05nr8if7',
        qa: '7loiuhiq3i9rtpjq4gkp2mmoir',
        staging: '6u7t9vatdk0tq130192280913h',
        prod: '1ca8tuisf8vnaffhfck2258idt',
      }),
      userPoolId: byEnv({
        dev: 'us-east-1_Y2KoOdwEL',
        qa: 'us-east-1_6d10P3Pok',
        staging: 'us-east-1_j3zqh6Snw',
        prod: 'us-east-1_BCVUfvjft',
      }),
      region: 'us-east-1',
    },
    braze: byEnv({
      prodOrQa: byRegion({
        US: {
          iOSKey: '1b6f5efc-ab52-435f-ac5e-ed883bc068a1',
          androidKey: '36e81a8b-9b78-4bda-88ae-315a7c5d185e',
          sdkEndpoint: 'sdk.iad-03.braze.com',
          initUrl: 'https://customer.iad-03.braze.com/api/v3',
        },
        CA: {
          iOSKey: 'f4d84edd-b5da-4f62-af87-673295674277',
          androidKey: '3bb4cf18-7584-480e-a9aa-6bb405edff17',
          sdkEndpoint: 'sdk.iad-03.braze.com',
          initUrl: 'https://customer.iad-03.braze.com/api/v3',
        },
      }),
      default: {
        iOSKey: 'e0d47194-e087-4e21-8645-bd965d9e6414',
        androidKey: '5864abfd-1d2a-4493-bf73-71f0f0680241',
        sdkEndpoint: 'sdk.iad-03.braze.com',
        initUrl: 'https://customer.iad-03.braze.com/api/v3',
      },
    }),
    bringg: {
      region: '6',
    },
    bundleIdentifiers: byRegion({
      US: {
        ios: {
          dev: 'com.emn8.mobilem8.native.bkrewards.dev',
          staging: 'com.emn8.mobilem8.native.bkrewards.staging',
          qa: 'com.emn8.mobilem8.native.bkrewards.qa',
          prod: 'com.emn8.mobilem8.native.bkrewards',
          'prod-internal': 'com.bk.us.prod-internal',
        },
        android: {
          dev: 'com.emn8.mobilem8.nativeapp.bk.dev',
          staging: 'com.emn8.mobilem8.nativeapp.bk.staging',
          qa: 'com.emn8.mobilem8.nativeapp.bk.qa',
          prod: 'com.emn8.mobilem8.nativeapp.bk',
        },
      },
      CA: {
        ios: {
          dev: 'com.tillster.bk-ca-mobile.dev',
          qa: 'com.tillster.bk-ca-mobile.qa',
          prod: 'com.tillster.bk-ca-mobile',
          'prod-internal': 'com.bk.ca.prod-internal',
        },
        android: {
          dev: 'com.emn8.mobilem8.nativeapp.bkca.dev',
          qa: 'com.emn8.mobilem8.nativeapp.bkca.qa',
          prod: 'com.emn8.mobilem8.nativeapp.bkca',
        },
      },
    }),
    country: byRegion({ US: 'US', CA: 'CA' }),
    datadog: {
      // for logging
      webClientToken: 'pub50f2d3f55b70d3d144c25234ee29993e',
      nativeAppClientToken: 'pubdfc8cabf280cf5d07df58e981dd3bc10',
      // likely unused unless we enable RUM again
      webRumApplicationId: '22f0262e-67bb-4ea4-a3ab-feaeba7bc96c',
      nativeAppRumApplicationId: '56922071-43a2-4a62-8a89-e408461e5a63',
    },
    enableNumericPhoneValidation: byRegion({
      US: false,
      CA: true,
    }),
    firstData: byRegion({
      US: {
        braintreeAuthorizationKey: byEnv({
          prodOrQa: 'production_9qtd66np_9yhqfxjsnyqf8bfp',
          default: 'sandbox_4xqfmzmm_26d4wvxhxr75dxdf',
        }),
        braintreeBillingAgreementDescription: 'For payments to Burger King',
        cashAppMerchantId: byEnv({
          prodOrQa: 'fda50c7e07c949dfa234e43928cf9bdf86168acef46a4af79b68094bab304206',
          default: '4b792f71600644569f591d73f662881f833d17b3da5a4931a4ed9ff6fbda5260',
        }),
        baseUrl: byEnv({
          prodOrQa: 'https://ucom-bk.fiservapis.com',
          default: 'https://ucom-bk-int.fiservapis.com',
        }),
      },
      // not used by CA
      CA: {
        braintreeAuthorizationKey: '',
        braintreeBillingAgreementDescription: '',
        cashAppMerchantId: '',
        baseUrl: byEnv({
          prodOrQa: '',
          default: '',
        }),
      },
    }),
    firebaseCloudMessagingSenderId: byRegion({
      US: '565340334914',
      CA: '543858519246',
    }),
    forter: {
      siteId: byRegion({
        US: byEnv({
          prodOrQa: '89ea0743f614',
          default: '6ebc7d54b003',
        }),
        CA: byEnv({
          prodOrQa: 'cd86966ceeb2',
          staging: '6ebc7d54b003',
          dev: 'f4773afbf910',
        }),
      }),
    },
    google: {
      paymentsEnvironment: byEnv({
        prodOrQa: 'prod',
        default: 'test',
      }),
      paymentsGateway: byRegion({
        US: 'firstdata',
        CA: byEnv({
          prodOrQa: 'chase',
          staging: 'adyen',
          dev: 'orbital',
        }),
      }),
      // TODO clean up: this is kind of confusing and should be reevaluated to what is relevant but is a direct port from sanity
      paymentsGatewayMerchantId: {
        ca: byEnv({
          prodOrQa: '521980',
          default: 'RestaurantBrandsInternational',
        }),
        us: byEnv({
          prodOrQa: '456031998994',
          default: '3176752955',
        }),
      },
      paymentsNetworks: ['AMEX', 'MASTERCARD', 'VISA', 'DISCOVER', 'JCB'],
      prepaidPaymentsGateway: byRegion({
        US: 'firstdata',
        CA: '',
      }),
      prepaidPaymentsGatewayMerchantId: {
        ca: '',
        us: byEnv({
          prodOrQa: '456031998994',
          default: '3176752955',
        }),
      },
    },
    googleTagManager: {
      containerId: byRegion({
        US: byEnv({
          prod: 'GTM-5LKPGT6',
          default: 'GTM-5K6Q2J7',
        }),
        CA: byEnv({
          prod: 'GTM-WF6FDGPR',
          default: 'GTM-W2S62B6X',
        }),
      }),
    },
    googleServiceWebClientId: byRegion({
      US: byEnv({
        prod: '565340334914-i4pbd71lo6paj8basi7ar7a80abn10e5.apps.googleusercontent.com',
        default: '317599397681-6ke40bo39sehdm9ua1289j44qg9q0813.apps.googleusercontent.com',
      }),
      CA: byEnv({
        prod: '543858519246-6r4mk41a9kq4pm71pu2uu29gtugaoi6m.apps.googleusercontent.com',
        default: '188738808780-lk2mllrprg3trnifip2u156ken8kuk4i.apps.googleusercontent.com',
      }),
    }),
    iosAppDomain: byRegion({ US: 'burgerking.app.link', CA: 'burgerkingca.app.link' }),
    iosBackgroundModes: byRegion({ US: ['location', 'fetch', 'remote-notification'], CA: [] }),
    pypestream: {
      scriptUrl: byEnv({
        prod: 'https://web.pypestream.com/api-launcher.js',
        default: 'https://web-sandbox.pypestream.com/api-launcher.js',
      }),
      appId: byEnv({
        prod: '671e8279-ccd4-4908-ab01-e4e953961334',
        default: 'e83389a6-78b2-43ed-905e-d4041d30faaf',
      }),
    },
    restaurants: {
      displayEnv: byEnv({
        prodOrQa: 'prod',
        default: 'dev',
      }),
      validMobileOrderingEnvs: byEnv({
        prodOrQa: ['alpha', 'beta', 'live'],
        default: ['dev'],
      }),
    },
    minimumSignupAge: byRegion({
      US: 16,
      CA: 14,
    }),
    orbital: byRegion({
      // not used by US
      US: {
        baseUrl: '',
        subscriberId: '',
        pieFormat: '',
        mode: '' as 'eFPE' | 'FPE',
      },
      CA: {
        baseUrl: byEnv({
          prodOrQa: 'https://safetechpageencryption.chasepaymentech.com',
          default: 'https://safetechpageencryptionvar.chasepaymentech.com',
        }),
        subscriberId: '750000001767',
        pieFormat: '04',
        mode: 'FPE' as 'eFPE' | 'FPE',
      },
    }),
    projectId: byRegion({
      US: 'e92f305c-13dc-4e56-9e67-1303d24e5401',
      CA: '186e2aa5-d983-499f-8dd8-fa626523a5d2',
    }),
    timeFormat: byRegion({ US: 'hh:mm aaaa', CA: '' }),
  };
}
